import { useState, useEffect, useCallback } from "react";
import {
    Box,
    Card,
    Typography,
    TextField,
    Button,
    Grid,
    Stack,
    CircularProgress,
    Alert,
    Divider,
    MenuItem,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Chip,
} from "@mui/material";
import PersonAddAltRoundedIcon from "@mui/icons-material/PersonAddAltRounded";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import MailOutlineRoundedIcon from "@mui/icons-material/MailOutlineRounded";
import CloudUploadRoundedIcon from "@mui/icons-material/CloudUploadRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import InsertDriveFileRoundedIcon from "@mui/icons-material/InsertDriveFileRounded";
import { inviteUser, getInvitesSent, revokeInvite } from "../api";

const MAX_DOC_SIZE = 5 * 1024 * 1024; // 5 MB
const ALLOWED_DOC_TYPES = [
    "image/jpeg",
    "image/jpg",
    "image/png",
    "image/webp",
    "application/pdf",
];

const KYC_FIELDS = [
    { key: "pan", label: "PAN Card" },
    { key: "aadhaar", label: "Aadhaar Card" },
    { key: "cheque", label: "Cancelled Cheque" },
];

const readFileAsDataUrl = (file) =>
    new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = () => reject(reader.error);
        reader.readAsDataURL(file);
    });

const ALL_ROLES = [
    { level: 2, name: "ADMIN_PARTNER" },
    { level: 3, name: "WHITE_LABEL" },
    { level: 4, name: "STATE_HEAD" },
    { level: 5, name: "SUPER_DISTRIBUTOR" },
    { level: 6, name: "MASTER_DISTRIBUTOR" },
    { level: 7, name: "DISTRIBUTOR" },
    { level: 8, name: "RETAILER" },
    { level: 9, name: "SHOPKEEPER" },
];

const GENDER_OPTS = ["Male", "Female", "Other"];

const initialForm = {
    roleLevel: "",
    firstName: "",
    lastName: "",
    email: "",
    mobileNumber: "",
    gender: "",
    address1: "",
    address2: "",
    city: "",
    state: "",
    pincode: "",
    dateOfBirth: "",
    panCardNumber: "",
    aadharNumber: "",
    nameOnAadhar: "",
    bankAccountNumber: "",
    ifscCode: "",
    depositLimit: "",
    withdrawDailyLimit: "",
};

function formatDate(val) {
    if (val == null) return "—";
    const d = new Date(val);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    });
}

export default function InviteUser() {
    const [form, setForm] = useState(initialForm);
    // KYC docs: { pan, aadhaar, cheque } -> { dataUrl, name, isImage }
    const [kycDocs, setKycDocs] = useState({
        pan: null,
        aadhaar: null,
        cheque: null,
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [inviteLink, setInviteLink] = useState("");
    const [copied, setCopied] = useState(false);
    const [invitations, setInvitations] = useState([]);
    const [invitesLoading, setInvitesLoading] = useState(true);
    const [copiedId, setCopiedId] = useState(null);
    const [revokingId, setRevokingId] = useState(null);
    const myLevel = parseInt(localStorage.getItem("roleLevel") || "1", 10);
    const availableRoles = ALL_ROLES.filter((r) => r.level > myLevel);

    const fetchInvites = useCallback(() => {
        setInvitesLoading(true);
        getInvitesSent(1, 50)
            .then(({ data }) => setInvitations(data.invitations || []))
            .catch(() => setInvitations([]))
            .finally(() => setInvitesLoading(false));
    }, []);

    useEffect(() => {
        fetchInvites();
    }, [fetchInvites]);

    const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const handleDocSelect = (key) => async (e) => {
        const file = e.target.files?.[0];
        // allow re-selecting the same file later
        e.target.value = "";
        if (!file) return;
        setError("");
        if (!ALLOWED_DOC_TYPES.includes(file.type)) {
            return setError(
                `${file.name}: unsupported file type. Use JPG, PNG, WEBP or PDF.`,
            );
        }
        if (file.size > MAX_DOC_SIZE) {
            return setError(`${file.name}: file is larger than 5 MB.`);
        }
        try {
            const dataUrl = await readFileAsDataUrl(file);
            setKycDocs((prev) => ({
                ...prev,
                [key]: {
                    dataUrl,
                    name: file.name,
                    isImage: file.type.startsWith("image/"),
                },
            }));
        } catch {
            setError(`Could not read ${file.name}. Please try again.`);
        }
    };

    const removeDoc = (key) => () =>
        setKycDocs((prev) => ({ ...prev, [key]: null }));

    const handleInvite = async () => {
        setError("");
        if (!form.roleLevel) return setError("Please select a role");
        if (!form.firstName || !form.lastName)
            return setError("First and last name are required");
        if (!form.email && !form.mobileNumber)
            return setError("Email or phone number is required");
        /* KYC documents are optional here — the invitee can upload them on the
           registration page. Whatever the inviter attaches is carried over. */
        setLoading(true);
        try {
            const { data } = await inviteUser({
                ...form,
                roleId: parseInt(form.roleLevel, 10),
                depositLimit: form.depositLimit
                    ? parseFloat(form.depositLimit)
                    : undefined,
                withdrawDailyLimit: form.withdrawDailyLimit
                    ? parseFloat(form.withdrawDailyLimit)
                    : undefined,
                kycDocuments: {
                    pan: kycDocs.pan?.dataUrl,
                    aadhaar: kycDocs.aadhaar?.dataUrl,
                    cheque: kycDocs.cheque?.dataUrl,
                },
            });
            setInviteLink(data.inviteLink || "");
            fetchInvites();
        } catch (e) {
            setError(e.response?.data?.error || "Failed to send invite");
        } finally {
            setLoading(false);
        }
    };

    const handleCopy = () => {
        navigator.clipboard.writeText(inviteLink);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const handleCopyLink = (inv) => {
        if (!inv.inviteLink) return;
        navigator.clipboard.writeText(inv.inviteLink);
        setCopiedId(inv.id);
        setTimeout(
            () => setCopiedId((c) => (c === inv.id ? null : c)),
            2000,
        );
    };

    const handleRevoke = async (inv) => {
        const who =
            [inv.first_name ?? inv.firstName, inv.last_name ?? inv.lastName]
                .filter(Boolean)
                .join(" ") ||
            inv.email ||
            inv.mobile_number ||
            "this user";
        if (
            !window.confirm(
                `Cancel the invite for ${who}? Their uploaded documents will be removed and you can invite them again.`,
            )
        )
            return;
        setRevokingId(inv.id);
        setError("");
        try {
            await revokeInvite(inv.id);
            fetchInvites();
        } catch (e) {
            setError(e.response?.data?.error || "Failed to cancel invite");
        } finally {
            setRevokingId(null);
        }
    };

    return (
        <Box>
            <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                mb={3}
            >
                <Box>
                    <Typography variant="h5">Add User</Typography>
                    <Typography variant="body2" color="text.secondary">
                        Send an invite to add someone to your network
                    </Typography>
                </Box>
            </Stack>

            <Card sx={{ p: { xs: 3, md: 4 } }}>
                {error && (
                    <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
                        {error}
                    </Alert>
                )}

                <Typography
                    variant="subtitle1"
                    fontWeight={600}
                    mb={2}
                    color="primary"
                >
                    Role & Limits
                </Typography>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={4}>
                        <TextField
                            fullWidth
                            select
                            label="Assign Role *"
                            value={form.roleLevel}
                            onChange={set("roleLevel")}
                        >
                            {availableRoles.map((r) => (
                                <MenuItem key={r.level} value={r.level}>
                                    {r.name.replace(/_/g, " ")} (Level {r.level}
                                    )
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <TextField
                            fullWidth
                            label="Deposit Limit"
                            type="number"
                            value={form.depositLimit}
                            onChange={set("depositLimit")}
                            placeholder="e.g. 500000"
                        />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <TextField
                            fullWidth
                            label="Daily Withdraw Limit"
                            type="number"
                            value={form.withdrawDailyLimit}
                            onChange={set("withdrawDailyLimit")}
                            placeholder="e.g. 50000"
                        />
                    </Grid>
                </Grid>

                <Divider sx={{ my: 3 }} />
                <Typography
                    variant="subtitle1"
                    fontWeight={600}
                    mb={2}
                    color="primary"
                >
                    Personal Information
                </Typography>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            label="First Name *"
                            value={form.firstName}
                            onChange={set("firstName")}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            label="Last Name *"
                            value={form.lastName}
                            onChange={set("lastName")}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            label="Email Address"
                            value={form.email}
                            onChange={set("email")}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            label="Mobile Number"
                            value={form.mobileNumber}
                            onChange={set("mobileNumber")}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            select
                            label="Gender"
                            value={form.gender}
                            onChange={set("gender")}
                        >
                            <MenuItem value="">—</MenuItem>
                            {GENDER_OPTS.map((g) => (
                                <MenuItem key={g} value={g}>
                                    {g}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            label="Date of Birth"
                            type="date"
                            value={form.dateOfBirth}
                            onChange={set("dateOfBirth")}
                            InputLabelProps={{ shrink: true }}
                        />
                    </Grid>
                </Grid>

                <Divider sx={{ my: 3 }} />
                <Typography
                    variant="subtitle1"
                    fontWeight={600}
                    mb={2}
                    color="primary"
                >
                    Address
                </Typography>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="Address Line 1"
                            value={form.address1}
                            onChange={set("address1")}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="Address Line 2"
                            value={form.address2}
                            onChange={set("address2")}
                        />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <TextField
                            fullWidth
                            label="City"
                            value={form.city}
                            onChange={set("city")}
                        />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <TextField
                            fullWidth
                            label="State"
                            value={form.state}
                            onChange={set("state")}
                        />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <TextField
                            fullWidth
                            label="Pincode"
                            value={form.pincode}
                            onChange={set("pincode")}
                        />
                    </Grid>
                </Grid>

                <Divider sx={{ my: 3 }} />
                <Typography
                    variant="subtitle1"
                    fontWeight={600}
                    mb={2}
                    color="primary"
                >
                    KYC Details
                </Typography>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={4}>
                        <TextField
                            fullWidth
                            label="PAN Card Number"
                            value={form.panCardNumber}
                            onChange={set("panCardNumber")}
                        />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <TextField
                            fullWidth
                            label="Aadhar Number"
                            value={form.aadharNumber}
                            onChange={set("aadharNumber")}
                        />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <TextField
                            fullWidth
                            label="Name on Aadhar"
                            value={form.nameOnAadhar}
                            onChange={set("nameOnAadhar")}
                        />
                    </Grid>
                </Grid>

                <Divider sx={{ my: 3 }} />
                <Typography
                    variant="subtitle1"
                    fontWeight={600}
                    mb={0.5}
                    color="primary"
                >
                    KYC Documents (optional)
                </Typography>
                <Typography variant="body2" color="text.secondary" mb={2}>
                    Optionally attach PAN card, Aadhaar card and a cancelled
                    cheque (JPG, PNG, WEBP or PDF, up to 5 MB each). If you skip
                    any, the invitee can upload them on their registration page —
                    all three are required before their account is reviewed.
                </Typography>
                <Grid container spacing={2}>
                    {KYC_FIELDS.map(({ key, label }) => {
                        const doc = kycDocs[key];
                        return (
                            <Grid item xs={12} sm={4} key={key}>
                                <Box
                                    sx={{
                                        border: "1px dashed",
                                        borderColor: doc
                                            ? "success.main"
                                            : "divider",
                                        borderRadius: 2,
                                        p: 2,
                                        textAlign: "center",
                                        height: "100%",
                                        display: "flex",
                                        flexDirection: "column",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        gap: 1,
                                    }}
                                >
                                    <Typography
                                        variant="body2"
                                        fontWeight={600}
                                    >
                                        {label} *
                                    </Typography>
                                    {doc ? (
                                        <>
                                            {doc.isImage ? (
                                                <Box
                                                    component="img"
                                                    src={doc.dataUrl}
                                                    alt={label}
                                                    sx={{
                                                        width: "100%",
                                                        maxHeight: 100,
                                                        objectFit: "contain",
                                                        borderRadius: 1,
                                                        bgcolor: "action.hover",
                                                    }}
                                                />
                                            ) : (
                                                <InsertDriveFileRoundedIcon
                                                    sx={{
                                                        fontSize: 40,
                                                        color: "text.secondary",
                                                    }}
                                                />
                                            )}
                                            <Typography
                                                variant="caption"
                                                color="text.secondary"
                                                sx={{
                                                    wordBreak: "break-all",
                                                }}
                                            >
                                                {doc.name}
                                            </Typography>
                                            <Stack
                                                direction="row"
                                                gap={1}
                                                justifyContent="center"
                                            >
                                                <Button
                                                    size="small"
                                                    component="label"
                                                    sx={{
                                                        textTransform: "none",
                                                    }}
                                                >
                                                    Replace
                                                    <input
                                                        hidden
                                                        type="file"
                                                        accept="image/*,application/pdf"
                                                        onChange={handleDocSelect(
                                                            key,
                                                        )}
                                                    />
                                                </Button>
                                                <Button
                                                    size="small"
                                                    color="error"
                                                    startIcon={
                                                        <DeleteOutlineRoundedIcon />
                                                    }
                                                    onClick={removeDoc(key)}
                                                    sx={{
                                                        textTransform: "none",
                                                    }}
                                                >
                                                    Remove
                                                </Button>
                                            </Stack>
                                        </>
                                    ) : (
                                        <Button
                                            component="label"
                                            variant="outlined"
                                            size="small"
                                            startIcon={<CloudUploadRoundedIcon />}
                                            sx={{
                                                textTransform: "none",
                                                borderRadius: 2,
                                            }}
                                        >
                                            Upload
                                            <input
                                                hidden
                                                type="file"
                                                accept="image/*,application/pdf"
                                                onChange={handleDocSelect(key)}
                                            />
                                        </Button>
                                    )}
                                </Box>
                            </Grid>
                        );
                    })}
                </Grid>

                <Divider sx={{ my: 3 }} />
                <Typography
                    variant="subtitle1"
                    fontWeight={600}
                    mb={2}
                    color="primary"
                >
                    Bank Details
                </Typography>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            label="Bank Account Number"
                            value={form.bankAccountNumber}
                            onChange={set("bankAccountNumber")}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            label="IFSC Code"
                            value={form.ifscCode}
                            onChange={set("ifscCode")}
                        />
                    </Grid>
                </Grid>

                <Button
                    variant="contained"
                    fullWidth
                    size="large"
                    onClick={handleInvite}
                    disabled={loading}
                    startIcon={
                        loading ? (
                            <CircularProgress size={20} color="inherit" />
                        ) : (
                            <PersonAddAltRoundedIcon />
                        )
                    }
                    sx={{ mt: 4, py: 1.5, borderRadius: 3, fontSize: 16 }}
                >
                    {loading ? "Sending Invite..." : "Send Invite"}
                </Button>
            </Card>

            {/* Invites I sent */}
            <Typography variant="h6" sx={{ mt: 4, mb: 2 }}>
                Invites I sent
            </Typography>
            <Card sx={{ overflow: "hidden" }}>
                {invitesLoading ? (
                    <Box sx={{ p: 3, textAlign: "center" }}>
                        <CircularProgress size={24} />
                    </Box>
                ) : invitations.length === 0 ? (
                    <Box sx={{ p: 4, textAlign: "center" }}>
                        <MailOutlineRoundedIcon
                            sx={{ fontSize: 40, color: "text.disabled", mb: 1 }}
                        />
                        <Typography color="text.secondary">
                            No invites sent yet
                        </Typography>
                    </Box>
                ) : (
                    <Box sx={{ overflowX: "auto" }}>
                    <Table size="small" sx={{ minWidth: 900 }}>
                        <TableHead>
                            <TableRow>
                                <TableCell>Name</TableCell>
                                <TableCell>Email / Phone</TableCell>
                                <TableCell>Role</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>Expires</TableCell>
                                <TableCell>Joined</TableCell>
                                <TableCell align="right">Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {invitations.map((inv) => {
                                const expiresAt =
                                    inv.expires_at ?? inv.expiresAt;
                                const isExpired = expiresAt
                                    ? new Date(expiresAt) <= new Date()
                                    : false;
                                const joined =
                                    (inv.status ?? "").toLowerCase() ===
                                    "accepted";
                                return (
                                    <TableRow key={inv.id} hover>
                                        <TableCell>
                                            <Typography
                                                variant="body2"
                                                fontWeight={500}
                                            >
                                                {inv.first_name ??
                                                    inv.firstName}{" "}
                                                {inv.last_name ?? inv.lastName}
                                            </Typography>
                                        </TableCell>
                                        <TableCell
                                            sx={{
                                                color: "text.secondary",
                                                fontSize: 13,
                                            }}
                                        >
                                            {inv.email ||
                                                inv.mobile_number ||
                                                "—"}
                                        </TableCell>
                                        <TableCell>
                                            <Typography
                                                variant="body2"
                                                fontSize={13}
                                            >
                                                {(
                                                    inv.role_name ??
                                                    inv.roleName ??
                                                    ""
                                                ).replace(/_/g, " ")}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                size="small"
                                                label={
                                                    inv.rejection_reason &&
                                                    (inv.status ?? "") ===
                                                        "pending"
                                                        ? "rejected — re-upload"
                                                        : (inv.status ??
                                                          "pending")
                                                }
                                                color={
                                                    inv.rejection_reason &&
                                                    (inv.status ?? "") ===
                                                        "pending"
                                                        ? "error"
                                                        : joined
                                                          ? "success"
                                                          : isExpired
                                                            ? "default"
                                                            : "warning"
                                                }
                                                sx={{
                                                    fontWeight: 600,
                                                    fontSize: 11,
                                                }}
                                            />
                                            {inv.rejection_reason && (
                                                <Typography
                                                    variant="caption"
                                                    color="error.main"
                                                    sx={{
                                                        display: "block",
                                                        mt: 0.5,
                                                    }}
                                                >
                                                    {inv.rejection_reason}
                                                </Typography>
                                            )}
                                        </TableCell>
                                        <TableCell
                                            sx={{
                                                fontSize: 13,
                                                color: isExpired
                                                    ? "error.main"
                                                    : "text.secondary",
                                            }}
                                        >
                                            {formatDate(expiresAt)}
                                            {isExpired &&
                                            inv.status === "pending"
                                                ? " (expired)"
                                                : ""}
                                        </TableCell>
                                        <TableCell>
                                            {joined ? (
                                                <Chip
                                                    size="small"
                                                    label="Yes"
                                                    color="success"
                                                    sx={{ fontWeight: 600 }}
                                                />
                                            ) : (
                                                <Typography
                                                    variant="caption"
                                                    color="text.secondary"
                                                >
                                                    No
                                                </Typography>
                                            )}
                                        </TableCell>
                                        <TableCell align="right">
                                            {joined ? (
                                                <Typography
                                                    variant="caption"
                                                    color="text.disabled"
                                                >
                                                    —
                                                </Typography>
                                            ) : (
                                                <Stack
                                                    direction="row"
                                                    spacing={0.5}
                                                    justifyContent="flex-end"
                                                >
                                                    {inv.inviteLink && (
                                                        <IconButton
                                                            size="small"
                                                            color={
                                                                copiedId ===
                                                                inv.id
                                                                    ? "success"
                                                                    : "default"
                                                            }
                                                            title={
                                                                copiedId ===
                                                                inv.id
                                                                    ? "Link copied!"
                                                                    : "Copy invite link"
                                                            }
                                                            onClick={() =>
                                                                handleCopyLink(
                                                                    inv,
                                                                )
                                                            }
                                                        >
                                                            {copiedId ===
                                                            inv.id ? (
                                                                <CheckCircleRoundedIcon fontSize="small" />
                                                            ) : (
                                                                <ContentCopyRoundedIcon fontSize="small" />
                                                            )}
                                                        </IconButton>
                                                    )}
                                                    <IconButton
                                                        size="small"
                                                        color="error"
                                                        title="Cancel invite"
                                                        disabled={
                                                            revokingId === inv.id
                                                        }
                                                        onClick={() =>
                                                            handleRevoke(inv)
                                                        }
                                                    >
                                                        {revokingId ===
                                                        inv.id ? (
                                                            <CircularProgress
                                                                size={16}
                                                            />
                                                        ) : (
                                                            <DeleteOutlineRoundedIcon fontSize="small" />
                                                        )}
                                                    </IconButton>
                                                </Stack>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                    </Box>
                )}
            </Card>

            {/* Invite Link Dialog */}
            <Dialog
                open={Boolean(inviteLink)}
                onClose={() => {
                    setInviteLink("");
                    setForm(initialForm);
                    setKycDocs({ pan: null, aadhaar: null, cheque: null });
                }}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle sx={{ textAlign: "center", pt: 4 }}>
                    <CheckCircleRoundedIcon
                        sx={{
                            fontSize: 56,
                            color: "success.main",
                            mb: 1,
                            display: "block",
                            mx: "auto",
                        }}
                    />
                    <Typography variant="h6" fontWeight={700}>
                        Invite Sent!
                    </Typography>
                </DialogTitle>
                <DialogContent>
                    <Typography
                        color="text.secondary"
                        textAlign="center"
                        mb={2}
                    >
                        Share this link with the user to complete their
                        registration:
                    </Typography>
                    <Stack
                        direction="row"
                        gap={1}
                        alignItems="center"
                        sx={{ p: 1.5, bgcolor: "action.hover", borderRadius: 2 }}
                    >
                        <Typography
                            variant="body2"
                            sx={{
                                flex: 1,
                                wordBreak: "break-all",
                                fontFamily: "monospace",
                                fontSize: 13,
                            }}
                        >
                            {inviteLink}
                        </Typography>
                        <IconButton
                            onClick={handleCopy}
                            color={copied ? "success" : "primary"}
                        >
                            {copied ? (
                                <CheckCircleRoundedIcon />
                            ) : (
                                <ContentCopyRoundedIcon />
                            )}
                        </IconButton>
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ justifyContent: "center", pb: 3 }}>
                    <Button
                        variant="contained"
                        onClick={() => {
                            setInviteLink("");
                            setForm(initialForm);
                            setKycDocs({ pan: null, aadhaar: null, cheque: null });
                            fetchInvites();
                        }}
                        sx={{ borderRadius: 3, px: 4 }}
                    >
                        Invite Another
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
